/*
  FuelBoxx storefront.
  - Reveal on scroll, header shadow, cart count.
  - <fbx-shop>: packs in the order, athlete details, live summary, and the
    handover to Shopify checkout with athlete details on every line item.
  - GA4 ecommerce events when a Measurement ID is set.
*/
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const currency = window.fbxCurrency || 'USD';
  const money = (cents) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: cents % 100 === 0 ? 0 : 2 }).format(cents / 100);

  const track = (event, params) => {
    if (typeof window.gtag === 'function') window.gtag('event', event, params);
  };

  /* ---------- Reveal on scroll ---------- */
  const revealAll = () => document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-in'));
  if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    revealAll();
  } else {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } }),
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    const watch = () => document.querySelectorAll('[data-reveal]:not(.is-in)').forEach((el) => io.observe(el));
    watch();
    document.addEventListener('shopify:section:load', watch);
    // Never leave content hidden if something goes wrong.
    setTimeout(revealAll, 4000);
  }

  /* ---------- Header ---------- */
  const header = document.querySelector('[data-fbx-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  const updateCartCount = async () => {
    try {
      const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
      document.querySelectorAll('[data-fbx-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
      document.querySelectorAll('[data-fbx-cart-link]').forEach((el) =>
        el.setAttribute('aria-label', `Cart, ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`)
      );
    } catch (e) { /* the count is a nicety; the cart page is always right */ }
  };

  /* ---------- Shop ---------- */
  const STORE_KEY = 'fbx-order-v1';
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  class FbxShop extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-fbx-packs]');
      try { this.packs = JSON.parse(data ? data.textContent : '{}'); } catch (e) { this.packs = {}; }
      this.maxAthletes = parseInt(this.dataset.maxAthletes || '4', 10);
      this.form = this.querySelector('[data-fbx-order-form]');
      this.athletesEl = this.querySelector('[data-fbx-athletes]');
      this.template = this.querySelector('[data-fbx-athlete-template]');
      this.addAthleteBtn = this.querySelector('[data-fbx-add-athlete]');
      this.linesEl = this.querySelector('[data-fbx-lines]');
      this.errorEl = this.querySelector('[data-fbx-form-error]');
      this.statusEl = this.querySelector('[data-fbx-status]');
      this.submitButtons = Array.from(this.querySelectorAll('[data-fbx-submit]'));
      this.uid = 0;

      const saved = this.restore();
      this.lines = saved.lines || {};
      // Drop anything no longer sold here, or now sold out.
      Object.keys(this.lines).forEach((key) => {
        const pack = this.packs[key];
        if (!pack || !pack.available) delete this.lines[key];
        else this.lines[key] = Math.min(this.lines[key], pack.max);
      });

      const athletes = saved.athletes && saved.athletes.length ? saved.athletes : [{}];
      athletes.slice(0, this.maxAthletes).forEach((a) => this.addAthlete(a, false));
      if (saved.email) {
        const email = this.querySelector('[data-fbx-guardian-email]');
        if (email) email.value = saved.email;
      }

      this.bar = this.querySelector('[data-fbx-orderbar]');
      this.orderInView = false;
      this.renderLines();
      this.bind();
      this.observeList();
      this.observeOrder();
    }

    /* The pinned order bar shows once something is in the order, except while
       the order panel itself is on screen. */
    observeOrder() {
      const order = this.querySelector('#order');
      if (!order || !('IntersectionObserver' in window)) return;
      new IntersectionObserver((entries) => {
        this.orderInView = entries.some((e) => e.isIntersecting);
        this.renderBar();
      }, { threshold: 0.05 }).observe(order);
    }

    renderBar() {
      if (!this.bar) return;
      const keys = Object.keys(this.lines).filter((k) => this.packs[k]);
      const count = keys.reduce((n, k) => n + this.lines[k], 0);
      const total = keys.reduce((n, k) => n + this.packs[k].price * this.lines[k], 0);
      this.bar.querySelector('[data-fbx-bar-count]').textContent = `${count} ${count === 1 ? 'pack' : 'packs'} · ${money(total)}`;
      const show = count > 0 && !this.orderInView;
      this.bar.hidden = !show;
      document.body.classList.toggle('fbx-has-orderbar', show);
    }

    /* Card: add the chosen quantity to the order without leaving the card. */
    addFromCard(key, qty, card) {
      const pack = this.packs[key];
      if (!pack || !pack.available) return;
      const before = this.lines[key] || 0;
      const next = Math.min(before + qty, pack.max);
      this.lines[key] = next;
      this.renderLines();
      this.persist();
      const added = next - before;
      const status = card.querySelector('[data-card-status]');
      if (status) {
        status.innerHTML = '';
        const msg = document.createElement('span');
        msg.textContent = added > 0 ? `Added ${added} to your order. ` : `You have the most we can send of this pack. `;
        const link = document.createElement('a');
        link.href = '#order'; link.textContent = 'Add athlete details';
        status.append(msg, link);
      }
      const out = card.querySelector('[data-card-qty]');
      if (out) out.textContent = '1';
      this.announce(added > 0 ? `${added} ${pack.title} added to your order.` : `${pack.title}: maximum reached.`);
      if (added > 0) track('add_to_cart', { currency, value: (pack.price * added) / 100, items: [this.gaItem(key, added, 0)] });
    }

    bind() {
      // Pack buttons anywhere on the page, including the closing call to action.
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-fbx-pack]');
        if (!trigger) return;
        const key = trigger.dataset.fbxPack;
        if (!this.packs[key]) return;
        event.preventDefault();
        this.choose(key, trigger);
      });

      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-card-step]');
        if (step) {
          const card = step.closest('[data-pack-card]');
          const out = card.querySelector('[data-card-qty]');
          const pack = this.packs[card.dataset.packCard];
          const max = pack ? Math.max(1, pack.max - (this.lines[card.dataset.packCard] || 0)) : 10;
          const next = Math.min(Math.max(1, parseInt(out.textContent, 10) + parseInt(step.dataset.cardStep, 10)), max);
          out.textContent = String(next);
          return;
        }
        const add = event.target.closest('[data-fbx-add]');
        if (add) this.addFromCard(add.dataset.fbxAdd, parseInt(add.closest('[data-pack-card]').querySelector('[data-card-qty]').textContent, 10) || 1, add.closest('[data-pack-card]'));
      });

      this.addAthleteBtn.addEventListener('click', () => {
        const el = this.addAthlete({}, true);
        if (el) el.querySelector('input').focus();
      });

      this.form.addEventListener('input', (e) => {
        const wrap = e.target.closest('[data-field-wrap], [data-athlete-sports], [data-consent-wrap]');
        if (wrap && wrap.classList.contains('is-invalid')) this.checkWrap(wrap);
        this.persist();
      });
      this.form.addEventListener('change', () => this.persist());
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(); });
    }

    observeList() {
      if (!('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        track('view_item_list', { item_list_name: this.dataset.listName, items: Object.keys(this.packs).map((k, i) => this.gaItem(k, 1, i)) });
      }, { threshold: 0.2 });
      const grid = this.querySelector('.fbx-packs');
      if (grid) io.observe(grid);
    }

    gaItem(key, quantity, index) {
      const p = this.packs[key];
      return { item_id: p.sku || String(p.variantId), item_name: p.title, item_brand: 'FuelBoxx', item_list_name: this.dataset.listName, index, price: p.price / 100, quantity };
    }

    /* Pack chosen: add it (or keep it) and take the parent to the order. */
    choose(key, trigger) {
      const pack = this.packs[key];
      if (!pack.available) return;
      const isNew = !this.lines[key];
      if (isNew) this.lines[key] = 1;
      this.renderLines();
      this.persist();
      this.announce(isNew ? `${pack.title} added to your order.` : `${pack.title} is already in your order. You can change the quantity in the summary.`);
      track('select_item', { item_list_name: this.dataset.listName, items: [this.gaItem(key, 1, 0)] });

      const order = this.querySelector('#order');
      if (order) {
        order.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        const line = this.linesEl.querySelector(`[data-line="${key}"]`);
        if (line) { line.classList.add('is-flash'); setTimeout(() => line.classList.remove('is-flash'), 1200); }
      }
      this.showError('');
    }

    setQty(key, qty) {
      const pack = this.packs[key];
      if (!pack) return;
      if (qty <= 0) delete this.lines[key];
      else this.lines[key] = Math.min(qty, pack.max);
      this.renderLines();
      this.persist();
    }

    renderLines() {
      const keys = Object.keys(this.lines).filter((k) => this.packs[k]);
      const empty = keys.length === 0;
      this.querySelector('[data-fbx-empty]').hidden = !empty;
      this.querySelector('[data-fbx-empty-link]').hidden = !empty;
      this.querySelector('[data-fbx-totals]').hidden = empty;

      this.linesEl.innerHTML = '';
      let subtotal = 0;
      keys.forEach((key) => {
        const p = this.packs[key];
        const qty = this.lines[key];
        subtotal += p.price * qty;
        const li = document.createElement('li');
        li.className = 'fbx-line';
        li.dataset.line = key;
        li.innerHTML = `
          ${p.image ? `<img class="fbx-line__img" src="${p.image}" alt="" width="56" height="56" loading="lazy">` : '<span class="fbx-line__img"></span>'}
          <div><p class="fbx-line__title"></p><p class="fbx-line__meta"></p></div>
          <span class="fbx-line__price">${money(p.price * qty)}</span>
          <div class="fbx-line__controls">
            <div class="fbx-qty" role="group">
              <button type="button" data-step="-1">&minus;</button>
              <output aria-live="polite">${qty}</output>
              <button type="button" data-step="1" ${qty >= p.max ? 'disabled' : ''}>+</button>
            </div>
            <button type="button" class="fbx-line__remove">Remove</button>
          </div>`;
        li.querySelector('.fbx-line__title').textContent = p.title;
        li.querySelector('.fbx-line__meta').textContent = [p.composition, `${money(p.price)} each`].filter(Boolean).join(' · ');
        const group = li.querySelector('.fbx-qty');
        group.setAttribute('aria-label', `Quantity of ${p.title}`);
        const [minus, plus] = li.querySelectorAll('[data-step]');
        minus.setAttribute('aria-label', qty === 1 ? `Remove ${p.title}` : `One fewer ${p.title}`);
        plus.setAttribute('aria-label', `One more ${p.title}`);
        minus.addEventListener('click', () => this.setQty(key, qty - 1));
        plus.addEventListener('click', () => this.setQty(key, qty + 1));
        const remove = li.querySelector('.fbx-line__remove');
        remove.setAttribute('aria-label', `Remove ${p.title} from your order`);
        remove.addEventListener('click', () => { this.setQty(key, 0); this.announce(`${p.title} removed.`); });
        this.linesEl.appendChild(li);
      });

      this.querySelector('[data-fbx-subtotal]').textContent = money(subtotal);
      this.querySelector('[data-fbx-total]').textContent = money(subtotal);

      // Clear a card's note once its pack leaves the order.
      document.querySelectorAll('[data-pack-card]').forEach((card) => {
        const status = card.querySelector('[data-card-status]');
        if (status && !this.lines[card.dataset.packCard]) status.textContent = '';
      });
      this.renderBar();
    }

    addAthlete(values, announce) {
      const count = this.athletesEl.querySelectorAll('[data-athlete]').length;
      if (count >= this.maxAthletes) return null;
      this.uid += 1;
      const id = `fbx-athlete-${this.dataset.sectionId}-${this.uid}`;
      const html = this.template.innerHTML.replace(/__ID__/g, id);
      const holder = document.createElement('div');
      holder.innerHTML = html;
      const el = holder.firstElementChild;
      const set = (name, v) => { const f = el.querySelector(`[data-athlete-field="${name}"]`); if (f && v) f.value = v; };
      set('first', values.first); set('last', values.last); set('allergies', values.allergies);
      const gf = el.querySelector('[data-athlete-field="gluten_free"]');
      if (gf && values.glutenFree) gf.checked = true;
      (values.sports || []).forEach((sport) => {
        el.querySelectorAll('[data-athlete-sport]').forEach((box) => { if (box.value === sport) box.checked = true; });
      });
      this.syncSports(el);
      el.querySelector('[data-athlete-remove]').addEventListener('click', () => {
        el.remove();
        this.renumber();
        this.persist();
        this.announce('Athlete removed.');
        this.addAthleteBtn.focus();
      });
      this.athletesEl.appendChild(el);
      this.renumber();
      if (announce) this.announce(`Athlete ${count + 1} added.`);
      return el;
    }

    renumber() {
      const all = this.athletesEl.querySelectorAll('[data-athlete]');
      all.forEach((el, i) => {
        el.querySelector('[data-athlete-number]').textContent = i + 1;
        el.querySelector('[data-athlete-remove]').hidden = all.length === 1;
      });
      this.addAthleteBtn.hidden = all.length >= this.maxAthletes;
    }

    readAthletes() {
      return Array.from(this.athletesEl.querySelectorAll('[data-athlete]')).map((el) => {
        const val = (n) => { const f = el.querySelector(`[data-athlete-field="${n}"]`); return f ? f.value.trim() : ''; };
        return {
          first: val('first'),
          last: val('last'),
          allergies: val('allergies'),
          glutenFree: Boolean(el.querySelector('[data-athlete-field="gluten_free"]:checked')),
          sports: Array.from(el.querySelectorAll('[data-athlete-sport]:checked')).map((b) => b.value),
        };
      });
    }

    // Sports dropdown: the closed control shows what is chosen.
    syncSports(el) {
      const out = el.querySelector('[data-sports-summary]');
      if (!out) return;
      const chosen = Array.from(el.querySelectorAll('[data-athlete-sport]:checked')).map((b) => b.value);
      out.textContent = chosen.length ? chosen.join(', ') : out.dataset.placeholder;
      out.classList.toggle('is-placeholder', !chosen.length);
    }

    /* ---------- Validation ---------- */
    checkWrap(wrap) {
      let ok = true;
      if (wrap.matches('[data-athlete-sports]')) {
        ok = !wrap.hasAttribute('data-required') || Boolean(wrap.querySelector('[data-athlete-sport]:checked'));
      } else if (wrap.matches('[data-consent-wrap]')) {
        ok = wrap.querySelector('input').checked;
      } else {
        const field = wrap.querySelector('input, textarea');
        if (field.required) ok = field.value.trim() !== '';
        if (ok && field.type === 'email' && field.value.trim() !== '') ok = EMAIL.test(field.value.trim());
        field.setAttribute('aria-invalid', ok ? 'false' : 'true');
      }
      wrap.classList.toggle('is-invalid', !ok);
      return ok;
    }

    validate() {
      let first = null;
      const wraps = this.form.querySelectorAll('[data-field-wrap], [data-athlete-sports], [data-consent-wrap]');
      wraps.forEach((wrap) => { if (!this.checkWrap(wrap) && !first) first = wrap; });

      if (Object.keys(this.lines).length === 0) {
        this.showError('Choose at least one pack to continue.');
        const shop = this.querySelector('.fbx-packs');
        if (shop) shop.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return false;
      }
      if (first) {
        this.showError('A few details need attention. They are marked below.');
        first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable = first.querySelector('input, textarea');
        if (focusable) focusable.focus({ preventScroll: true });
        return false;
      }
      this.showError('');
      return true;
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.classList.toggle('is-visible', Boolean(message));
    }

    announce(message) {
      if (!this.statusEl) return;
      this.statusEl.textContent = '';
      requestAnimationFrame(() => { this.statusEl.textContent = message; });
    }

    /* ---------- Persistence: survive a reload or a back button from checkout ---------- */
    persist() {
      try {
        const email = this.querySelector('[data-fbx-guardian-email]');
        sessionStorage.setItem(STORE_KEY, JSON.stringify({ lines: this.lines, athletes: this.readAthletes(), email: email ? email.value : '' }));
      } catch (e) { /* private mode: the form still works, it just will not remember */ }
    }

    restore() {
      try { return JSON.parse(sessionStorage.getItem(STORE_KEY) || '{}'); } catch (e) { return {}; }
    }

    /* ---------- Checkout ---------- */
    buildProperties(athletes) {
      const named = athletes.length > 1;
      const name = (a) => `${a.first} ${a.last}`.trim();
      const join = (fn) => athletes.map((a) => (named ? `${a.first}: ${fn(a)}` : fn(a))).join('; ');
      const props = { [athletes.length > 1 ? 'Athletes' : 'Athlete']: athletes.map(name).join(', ') };
      if (this.querySelector('[data-athlete-sports]')) props.Sports = join((a) => a.sports.join(', ') || 'Not given');
      if (this.querySelector('[data-athlete-field="allergies"]')) props['Allergies or dietary needs'] = join((a) => a.allergies || 'Not given');
      if (this.querySelector('[data-athlete-field="gluten_free"]')) props['Gluten-free option'] = join((a) => (a.glutenFree ? 'Yes' : 'No'));
      props._source = this.dataset.source;
      return props;
    }

    async submit() {
      if (this.busy) return;
      if (!this.validate()) return;
      this.busy = true;
      const labels = this.submitButtons.map((b) => b.textContent);
      this.submitButtons.forEach((b) => { b.disabled = true; b.textContent = 'Preparing checkout'; });
      const restore = () => {
        this.busy = false;
        this.submitButtons.forEach((b, i) => { b.disabled = false; b.textContent = labels[i]; });
      };

      try {
        const athletes = this.readAthletes();
        const properties = this.buildProperties(athletes);
        const json = { 'Content-Type': 'application/json', Accept: 'application/json' };

        // Clear anything this storefront added earlier, so a parent who comes
        // back never pays twice. Other products in the cart are left alone.
        const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
        const updates = {};
        cart.items.forEach((item) => { if (item.properties && item.properties._source === this.dataset.source) updates[item.key] = 0; });
        if (Object.keys(updates).length) {
          const cleared = await fetch(root + 'cart/update.js', { method: 'POST', headers: json, body: JSON.stringify({ updates }) });
          if (!cleared.ok) throw new Error('');
        }

        const attributes = { School: this.dataset.school, Source: this.dataset.source };
        athletes.forEach((a, i) => {
          attributes[`Athlete ${i + 1}`] = [`${a.first} ${a.last}`.trim(), a.sports.length ? `Sports: ${a.sports.join(', ')}` : '', a.allergies ? `Allergies or dietary needs: ${a.allergies}` : '', a.glutenFree ? 'Gluten-free option' : '']
            .filter(Boolean).join(' | ');
        });
        // One plain Yes/No field so a Shopify Flow rule can tag orders that need
        // an allergy check before packing. Anything other than a clear "none"
        // counts as Yes: over-flagging is the safe direction.
        const noAllergies = /^(none|no|nope|n\/?a|nil|nothing|no allergies)\.?$/i;
        // The gluten-free option also flags the order, so it is checked before packing.
        attributes['Allergy check'] = athletes.some((a) => (a.allergies && !noAllergies.test(a.allergies.trim())) || a.glutenFree) ? 'Yes' : 'No';
        if (this.querySelector('[data-athlete-field="gluten_free"]')) attributes['Gluten-free option'] = athletes.some((a) => a.glutenFree) ? 'Yes' : 'No';
        const email = this.querySelector('[data-fbx-guardian-email]');
        if (email) attributes['Parent or guardian email'] = email.value.trim();
        this.querySelectorAll('[data-fbx-consent]').forEach((box) => { attributes[box.dataset.fbxConsent] = box.checked ? 'Yes' : 'No'; });

        const stamped = await fetch(root + 'cart/update.js', { method: 'POST', headers: json, body: JSON.stringify({ attributes }) });
        if (!stamped.ok) throw new Error('');

        const keys = Object.keys(this.lines);
        const items = keys.map((key) => ({ id: this.packs[key].variantId, quantity: this.lines[key], properties }));
        const added = await fetch(root + 'cart/add.js', { method: 'POST', headers: json, body: JSON.stringify({ items }) });
        if (!added.ok) {
          const body = await added.json().catch(() => ({}));
          throw new Error(body.description || body.message || '');
        }

        const value = keys.reduce((sum, k) => sum + this.packs[k].price * this.lines[k], 0) / 100;
        const gaItems = keys.map((k, i) => this.gaItem(k, this.lines[k], i));
        track('add_to_cart', { currency, value, items: gaItems });
        track('begin_checkout', { currency, value, items: gaItems });

        window.location.href = root + 'checkout';
      } catch (error) {
        restore();
        updateCartCount();
        this.showError(error.message || 'We could not start checkout. Please check your connection and try again.');
        this.errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  if (!customElements.get('fbx-shop')) customElements.define('fbx-shop', FbxShop);

  /* ---------- <fbx-buy>: one purchase module ---------- */
  class FbxBuy extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-fbx-packs]');
      try { this.packs = JSON.parse(data ? data.textContent : '{}'); } catch (e) { this.packs = {}; }
      this.maxAthletes = parseInt(this.dataset.maxAthletes || '4', 10);
      this.storeKey = 'fbx-buy-v1';
      this.template = this.querySelector('[data-athlete-template]');
      this.list = this.querySelector('[data-athletes]');
      this.addBtn = this.querySelector('[data-add-athlete]');
      this.qtyOut = this.querySelector('[data-qty]');
      this.button = this.querySelector('[data-buy]');
      this.label = this.querySelector('[data-buy-label]');
      this.errorEl = this.querySelector('[data-buy-error]');
      this.confirm = this.querySelector('[data-confirm]');
      this.counter = 0;

      const saved = this.restore();
      if (saved && saved.pack) {
        const radio = this.querySelector(`input[type="radio"][value="${saved.pack}"]:not(:disabled)`);
        if (radio) radio.checked = true;
      }
      this.qty = saved && saved.qty ? saved.qty : 1;
      const athletes = saved && saved.athletes && saved.athletes.length ? saved.athletes : [{}];
      athletes.forEach((a) => this.addAthlete(a, false));
      if (saved && saved.confirm) this.confirm.checked = true;

      this.addEventListener('change', (event) => {
        if (event.target.matches('input[type="radio"]')) {
          this.qty = Math.min(this.qty, this.maxFor(this.selected()));
          track('select_item', { item_list_name: this.dataset.listName, items: [this.gaItem(this.selected(), 1, 0)] });
        }
        this.clearError(event.target);
        this.render();
        this.persist();
      });
      this.addEventListener('input', (event) => { this.clearError(event.target); this.persist(); });
      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-qty-step]');
        if (step) {
          this.qty = Math.max(1, Math.min(this.maxFor(this.selected()), this.qty + parseInt(step.dataset.qtyStep, 10)));
          this.render(); this.persist();
          return;
        }
        const remove = event.target.closest('[data-remove-athlete]');
        if (remove) { remove.closest('[data-athlete]').remove(); this.renumber(); this.persist(); return; }
      });
      this.addBtn.addEventListener('click', () => {
        const row = this.addAthlete({}, true);
        if (row) row.querySelector('input').focus();
      });
      this.button.addEventListener('click', () => this.submit());
      this.render();

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io.disconnect();
            track('view_item_list', { item_list_name: this.dataset.listName, items: Object.keys(this.packs).map((k, i) => this.gaItem(k, 1, i)) });
          }
        }, { threshold: 0.25 });
        io.observe(this);
      }
    }

    selected() {
      const r = this.querySelector('input[type="radio"]:checked');
      return r ? r.value : '';
    }

    maxFor(key) { return this.packs[key] ? Math.max(1, this.packs[key].max) : 10; }

    gaItem(key, quantity, index) {
      const p = this.packs[key] || {};
      return { item_id: p.sku || String(p.variantId || key), item_name: p.title || key, price: (p.price || 0) / 100, quantity, index };
    }

    addAthlete(values, persist) {
      const count = this.list.querySelectorAll('[data-athlete]').length;
      if (count >= this.maxAthletes) return null;
      this.counter += 1;
      const id = `fbx-athlete-${this.counter}`;
      const html = this.template.innerHTML.replace(/__ID__/g, id);
      const wrap = document.createElement('div');
      wrap.innerHTML = html.trim();
      const row = wrap.firstElementChild;
      row.querySelector('[data-athlete-field="name"]').value = values.name || '';
      row.querySelector('[data-athlete-field="allergies"]').value = values.allergies || '';
      this.list.appendChild(row);
      this.renumber();
      if (persist) this.persist();
      return row;
    }

    renumber() {
      const rows = [...this.list.querySelectorAll('[data-athlete]')];
      rows.forEach((row, i) => {
        row.querySelector('[data-athlete-title]').textContent = rows.length > 1 ? `Athlete ${i + 1}` : '';
        row.querySelector('[data-remove-athlete]').hidden = rows.length === 1;
        row.classList.toggle('is-single', rows.length === 1);
      });
      this.addBtn.hidden = rows.length >= this.maxAthletes;
    }

    readAthletes() {
      return [...this.list.querySelectorAll('[data-athlete]')].map((row) => ({
        name: row.querySelector('[data-athlete-field="name"]').value.trim(),
        allergies: row.querySelector('[data-athlete-field="allergies"]').value.trim(),
      }));
    }

    render() {
      this.qtyOut.textContent = String(this.qty);
      const key = this.selected();
      const p = this.packs[key];
      this.querySelectorAll('[data-qty-step]').forEach((b) => {
        b.disabled = b.dataset.qtyStep === '-1' ? this.qty <= 1 : this.qty >= this.maxFor(key);
      });
      this.label.textContent = p ? `${this.dataset.buttonLabel} · ${money(p.price * this.qty)}` : this.dataset.buttonLabel;
      this.button.disabled = !p;
    }

    persist() {
      try {
        sessionStorage.setItem(this.storeKey, JSON.stringify({ pack: this.selected(), qty: this.qty, athletes: this.readAthletes(), confirm: this.confirm.checked }));
      } catch (e) { /* private mode */ }
    }

    restore() {
      try { return JSON.parse(sessionStorage.getItem(this.storeKey) || 'null'); } catch (e) { return null; }
    }

    clearError(el) {
      const wrap = el && el.closest && el.closest('[data-field-wrap], [data-confirm-wrap]');
      if (wrap) wrap.classList.remove('is-invalid');
      if (!this.errorEl.hidden && !this.querySelector('.is-invalid')) this.errorEl.hidden = true;
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    validate() {
      let first = null;
      this.list.querySelectorAll('[data-athlete] [data-field-wrap]').forEach((wrap) => {
        const input = wrap.querySelector('input');
        const bad = !input.value.trim();
        wrap.classList.toggle('is-invalid', bad);
        input.setAttribute('aria-invalid', String(bad));
        if (bad && !first) first = input;
      });
      const confirmWrap = this.querySelector('[data-confirm-wrap]');
      confirmWrap.classList.toggle('is-invalid', !this.confirm.checked);
      if (!this.confirm.checked && !first) first = this.confirm;
      if (!this.selected()) { this.showError('Choose a pack to continue.'); return false; }
      if (first) {
        this.showError('Please complete the highlighted details.');
        first.focus({ preventScroll: true });
        first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
      this.errorEl.hidden = true;
      return true;
    }

    async submit() {
      if (this.busy || !this.validate()) return;
      this.busy = true;
      const before = this.label.textContent;
      this.button.disabled = true;
      this.label.textContent = 'Preparing checkout';
      const restoreBtn = () => { this.busy = false; this.button.disabled = false; this.label.textContent = before; };
      try {
        const key = this.selected();
        const pack = this.packs[key];
        const athletes = this.readAthletes();
        const json = { 'Content-Type': 'application/json', Accept: 'application/json' };

        // Clear anything this storefront added earlier so a parent who comes back never pays twice.
        const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
        const updates = {};
        cart.items.forEach((item) => { if (item.properties && item.properties._source === this.dataset.source) updates[item.key] = 0; });
        if (Object.keys(updates).length) {
          const cleared = await fetch(root + 'cart/update.js', { method: 'POST', headers: json, body: JSON.stringify({ updates }) });
          if (!cleared.ok) throw new Error('');
        }

        const noAllergies = /^(none|no|nope|n\/?a|nil|nothing|no allergies)\.?$/i;
        const attributes = { School: this.dataset.school, Source: this.dataset.source };
        athletes.forEach((a, i) => { attributes[`Athlete ${i + 1}`] = `${a.name} | Allergies or dietary needs: ${a.allergies}`; });
        // Clear athletes left over from an earlier, longer attempt in this cart.
        for (let i = athletes.length + 1; i <= 6; i += 1) attributes[`Athlete ${i}`] = '';
        // Plain Yes/No for the Shopify Flow rule that flags orders needing an allergy check.
        attributes['Allergy check'] = athletes.some((a) => !noAllergies.test(a.allergies)) ? 'Yes' : 'No';
        attributes['Parent or guardian confirmation'] = 'Yes';
        const stamped = await fetch(root + 'cart/update.js', { method: 'POST', headers: json, body: JSON.stringify({ attributes }) });
        if (!stamped.ok) throw new Error('');

        const multi = athletes.length > 1;
        const properties = {
          Athletes: athletes.map((a) => a.name).join(', '),
          'Allergies or dietary needs': athletes.map((a) => (multi ? `${a.name}: ${a.allergies}` : a.allergies)).join('; '),
          _source: this.dataset.source,
        };
        const added = await fetch(root + 'cart/add.js', { method: 'POST', headers: json, body: JSON.stringify({ items: [{ id: pack.variantId, quantity: this.qty, properties }] }) });
        if (!added.ok) {
          const body = await added.json().catch(() => ({}));
          throw new Error(body.description || body.message || '');
        }
        const value = (pack.price * this.qty) / 100;
        const items = [this.gaItem(key, this.qty, 0)];
        track('add_to_cart', { currency, value, items });
        track('begin_checkout', { currency, value, items });
        window.location.href = root + 'checkout';
      } catch (error) {
        restoreBtn();
        updateCartCount();
        this.showError(error.message || 'We could not start checkout. Please check your connection and try again.');
      }
    }
  }
  if (!customElements.get('fbx-buy')) customElements.define('fbx-buy', FbxBuy);

  // Links that name a pack (data-fbx-pack) choose it in the purchase module.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-fbx-pack]');
    const buy = document.querySelector('fbx-buy');
    if (!link || !buy) return;
    const radio = buy.querySelector(`input[type="radio"][value="${link.dataset.fbxPack}"]:not(:disabled)`);
    if (!radio) return;
    event.preventDefault();
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
    buy.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });



  // Fold: teaser height until opened. Without this script everything simply shows.
  document.querySelectorAll('[data-fbx-fold]').forEach((fold) => {
    const body = fold.querySelector('[data-fold-body]');
    const btn = fold.querySelector('[data-fold-toggle]');
    const label = btn.querySelector('[data-fold-label]');
    fold.classList.add('is-ready');
    btn.hidden = false;
    btn.addEventListener('click', () => {
      const open = !fold.classList.contains('is-open');
      if (open) {
        body.style.maxHeight = body.scrollHeight + 'px';
        fold.classList.add('is-open');
        const done = () => { body.style.maxHeight = 'none'; body.removeEventListener('transitionend', done); };
        body.addEventListener('transitionend', done);
        setTimeout(done, 600);
      } else {
        body.style.maxHeight = body.scrollHeight + 'px';
        requestAnimationFrame(() => { fold.classList.remove('is-open'); body.style.maxHeight = ''; });
        const top = fold.closest('section').getBoundingClientRect().top;
        if (top < 0) fold.closest('section').scrollIntoView({ block: 'start' });
      }
      btn.setAttribute('aria-expanded', String(open));
      label.textContent = open ? btn.dataset.labelOpen : btn.dataset.labelClosed;
    });
  });

  // Swipe tracks (phones): dots, arrows and tabs follow the card in view.
  document.querySelectorAll('[data-fbx-swipe]').forEach((wrap) => {
    const track = wrap.querySelector('[data-swipe-track]');
    if (!track) return;
    const items = [...track.children];
    const dotsBox = wrap.querySelector('[data-swipe-dots]');
    const dots = dotsBox ? items.map((_, i) => {
      const d = document.createElement('button');
      d.type = 'button'; d.className = 'fbx-swipe-dot'; d.setAttribute('aria-label', `${i + 1} of ${items.length}`);
      d.addEventListener('click', () => go(i));
      dotsBox.appendChild(d);
      return d;
    }) : [];
    const tabs = [...wrap.querySelectorAll('[data-swipe-to]')];
    const arrows = [...wrap.querySelectorAll('[data-swipe-step]')];
    let current = 0;
    const go = (i) => {
      const target = items[Math.max(0, Math.min(items.length - 1, i))];
      track.scrollTo({ left: target.offsetLeft - items[0].offsetLeft, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    };
    const sync = () => {
      const x = track.scrollLeft;
      let best = 0;
      items.forEach((el, i) => { if (Math.abs(el.offsetLeft - items[0].offsetLeft - x) < Math.abs(items[best].offsetLeft - items[0].offsetLeft - x)) best = i; });
      if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 4) best = items.length - 1;
      current = best;
      items.forEach((el, i) => el.classList.toggle('is-current', i === best));
      dots.forEach((d, i) => d.setAttribute('aria-current', String(i === best)));
      tabs.forEach((t, i) => t.setAttribute('aria-selected', String(i === best)));
      arrows.forEach((a) => { a.disabled = (a.dataset.swipeStep === '-1' && best === 0) || (a.dataset.swipeStep === '1' && best === items.length - 1); });
    };
    tabs.forEach((t) => t.addEventListener('click', () => go(parseInt(t.dataset.swipeTo, 10))));
    arrows.forEach((a) => a.addEventListener('click', () => go(current + parseInt(a.dataset.swipeStep, 10))));
    let raf = 0;
    track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(sync); }, { passive: true });
    sync();
  });

  // Sports dropdown: keep the summary current, close with Done or a tap outside.
  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-athlete-sport]')) return;
    const shop = event.target.closest('fbx-shop');
    if (shop && shop.syncSports) shop.syncSports(event.target.closest('[data-athlete]'));
  });
  document.addEventListener('click', (event) => {
    const done = event.target.closest('[data-sports-done]');
    if (done) { const d = done.closest('details'); d.open = false; d.querySelector('summary').focus(); return; }
    document.querySelectorAll('[data-sports-dropdown][open]').forEach((d) => { if (!d.contains(event.target)) d.open = false; });
  });

  // Shorter pack cards on phones: Pack details opens the rest of a card.
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-pack-details]');
    if (!btn) return;
    const card = btn.closest('[data-pack-card]');
    const open = card.classList.toggle('is-expanded');
    btn.setAttribute('aria-expanded', String(open));
    btn.querySelector('[data-pack-details-label]').textContent = open ? 'Hide details' : 'Pack details';
  });

  // Footer policies list: close it on a click elsewhere or on Escape.
  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-fbx-policies][open]').forEach((d) => { if (!d.contains(event.target)) d.open = false; });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-fbx-policies][open]').forEach((d) => { d.open = false; d.querySelector('summary').focus(); });
  });

  /* Plain anchor links to #order or #shop from other sections. */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link || link.hasAttribute('data-fbx-pack')) return;
    const target = document.getElementById(link.getAttribute('href').slice(1));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(null, '', link.getAttribute('href'));
  });
})();
